import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  Store,
  LocalOffer,
  Visibility,
  Star,
  AttachMoney,
  CheckCircle,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface AnalyticsData {
  totalUsers: number;
  totalVendors: number;
  totalOffers: number;
  totalRevenue: number;
  userGrowth: number;
  vendorGrowth: number;
  offerGrowth: number;
  revenueGrowth: number;
}

interface ChartData {
  name: string;
  users: number;
  vendors: number;
  offers: number;
  revenue: number;
}

interface TopVendor {
  id: string;
  name: string;
  offers: number;
  revenue: number;
  rating: number;
}

const Analytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    totalUsers: 0,
    totalVendors: 0,
    totalOffers: 0,
    totalRevenue: 0,
    userGrowth: 0,
    vendorGrowth: 0,
    offerGrowth: 0,
    revenueGrowth: 0,
  });
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [topVendors, setTopVendors] = useState<TopVendor[]>([]);
  const [timeRange, setTimeRange] = useState('30');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/analytics?range=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      if (data.success) {
        setAnalyticsData({
          totalUsers: data.data.totalUsers || 0,
          totalVendors: data.data.totalVendors || 0,
          totalOffers: data.data.totalOffers || 0,
          totalRevenue: data.data.totalRevenue || 0,
          userGrowth: data.data.userGrowth || 0,
          vendorGrowth: data.data.vendorGrowth || 0,
          offerGrowth: data.data.offerGrowth || 0,
          revenueGrowth: data.data.revenueGrowth || 0,
        });
      } else {
        // Fallback to mock data
        setAnalyticsData({
          totalUsers: 1250,
          totalVendors: 340,
          totalOffers: 2100,
          totalRevenue: 45600,
          userGrowth: 12.5,
          vendorGrowth: 8.3,
          offerGrowth: 15.2,
          revenueGrowth: 22.1,
        });
      }

      // Mock chart data
      const mockChartData: ChartData[] = [
        { name: 'Jan', users: 1000, vendors: 280, offers: 1500, revenue: 35000 },
        { name: 'Feb', users: 1100, vendors: 300, offers: 1700, revenue: 38000 },
        { name: 'Mar', users: 1200, vendors: 320, offers: 1900, revenue: 41000 },
        { name: 'Apr', users: 1250, vendors: 340, offers: 2100, revenue: 45600 },
      ];
      setChartData(mockChartData);

      // Mock top vendors
      const mockTopVendors: TopVendor[] = [
        { id: '1', name: 'Pizza Palace', offers: 15, revenue: 2450, rating: 4.5 },
        { id: '2', name: 'Fashion Store', offers: 12, revenue: 1890, rating: 4.2 },
        { id: '3', name: 'Tech Solutions', offers: 8, revenue: 1200, rating: 4.0 },
        { id: '4', name: 'Coffee Corner', offers: 6, revenue: 980, rating: 4.3 },
        { id: '5', name: 'Gym Fitness', offers: 10, revenue: 1500, rating: 4.1 },
      ];
      setTopVendors(mockTopVendors);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getGrowthColor = (growth: number) => {
    return growth >= 0 ? 'success.main' : 'error.main';
  };

  const getGrowthIcon = (growth: number) => {
    return growth >= 0 ? <TrendingUp /> : <TrendingDown />;
  };

  const pieData = [
    { name: 'Restaurant', value: 45, color: '#8884d8' },
    { name: 'Retail', value: 30, color: '#82ca9d' },
    { name: 'Service', value: 15, color: '#ffc658' },
    { name: 'Entertainment', value: 10, color: '#ff7300' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Platform performance and insights
          </Typography>
        </Box>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
            <MenuItem value="365">Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Users</Typography>
              </Box>
              <Typography variant="h4" color="primary" gutterBottom>
                {formatNumber(analyticsData.totalUsers)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getGrowthIcon(analyticsData.userGrowth)}
                <Typography
                  variant="body2"
                  color={getGrowthColor(analyticsData.userGrowth)}
                  sx={{ ml: 1 }}
                >
                  {analyticsData.userGrowth > 0 ? '+' : ''}{analyticsData.userGrowth}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Store color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Vendors</Typography>
              </Box>
              <Typography variant="h4" color="secondary.main" gutterBottom>
                {formatNumber(analyticsData.totalVendors)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getGrowthIcon(analyticsData.vendorGrowth)}
                <Typography
                  variant="body2"
                  color={getGrowthColor(analyticsData.vendorGrowth)}
                  sx={{ ml: 1 }}
                >
                  {analyticsData.vendorGrowth > 0 ? '+' : ''}{analyticsData.vendorGrowth}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalOffer color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Offers</Typography>
              </Box>
              <Typography variant="h4" color="success.main" gutterBottom>
                {formatNumber(analyticsData.totalOffers)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getGrowthIcon(analyticsData.offerGrowth)}
                <Typography
                  variant="body2"
                  color={getGrowthColor(analyticsData.offerGrowth)}
                  sx={{ ml: 1 }}
                >
                  {analyticsData.offerGrowth > 0 ? '+' : ''}{analyticsData.offerGrowth}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AttachMoney color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Revenue</Typography>
              </Box>
              <Typography variant="h4" color="warning.main" gutterBottom>
                {formatCurrency(analyticsData.totalRevenue)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getGrowthIcon(analyticsData.revenueGrowth)}
                <Typography
                  variant="body2"
                  color={getGrowthColor(analyticsData.revenueGrowth)}
                  sx={{ ml: 1 }}
                >
                  {analyticsData.revenueGrowth > 0 ? '+' : ''}{analyticsData.revenueGrowth}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '2 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Growth Trends
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="users" stroke="#8884d8" strokeWidth={2} />
                    <Line type="monotone" dataKey="vendors" stroke="#82ca9d" strokeWidth={2} />
                    <Line type="monotone" dataKey="offers" stroke="#ffc658" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Vendor Categories
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Revenue Chart */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 100%' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Trends
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Area type="monotone" dataKey="revenue" stroke="#8884d8" fill="#8884d8" />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Top Vendors */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '2 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Performing Vendors
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Offers</TableCell>
                      <TableCell>Revenue</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topVendors.map((vendor) => (
                      <TableRow key={vendor.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Store sx={{ mr: 1, color: 'primary.main' }} />
                            {vendor.name}
                          </Box>
                        </TableCell>
                        <TableCell>{vendor.offers}</TableCell>
                        <TableCell>{formatCurrency(vendor.revenue)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Star sx={{ mr: 0.5, fontSize: 16, color: 'orange' }} />
                            {vendor.rating}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label="Active" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="New vendor approved"
                    secondary="Pizza Palace joined the platform"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocalOffer color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="New offer created"
                    secondary="50% off Pizza deal"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <People color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="User registration"
                    secondary="25 new users joined today"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Visibility color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="High engagement"
                    secondary="Fashion Store offer trending"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Analytics;
